import { useState } from 'react';
import { Clock3, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  formatDateTime,
  type DepotApproval,
  type DepotAuditEntry,
  type DepotAuditResponse,
  type DepotContainerVisit,
  useDepotList,
} from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

export default function DepotContainersPage() {
  const { items, loading, error, refresh } = useDepotList<DepotContainerVisit>('/depot/containers?limit=100');
  const [historyContainer, setHistoryContainer] = useState<DepotContainerVisit | null>(null);
  const [historyItems, setHistoryItems] = useState<DepotAuditEntry[]>([]);
  const [historyApprovals, setHistoryApprovals] = useState<DepotApproval[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [releasingVisit, setReleasingVisit] = useState<DepotContainerVisit | null>(null);
  const [targetConditionStatus, setTargetConditionStatus] = useState<'sound' | 'damaged'>('sound');
  const [releaseNote, setReleaseNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [requestingApproval, setRequestingApproval] = useState(false);

  const pageError: DataErrorDetail | null = error
    ? { endpoint: '/depot/containers', message: error, status: 500, code: 'DEPOT_CONTAINERS_LOAD_FAILED' }
    : null;

  const openHistory = async (item: DepotContainerVisit) => {
    setHistoryContainer(item);
    setHistoryLoading(true);
    try {
      const result = await api.get<DepotAuditResponse>(`/depot/containers/${item.container_no}/audit-trail`);
      setHistoryItems(result.items ?? []);
      setHistoryApprovals(result.approvals ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load container history');
      setHistoryItems([]);
      setHistoryApprovals([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openReleaseHold = (item: DepotContainerVisit) => {
    setReleasingVisit(item);
    setTargetConditionStatus(item.condition_status === 'repair_required' || item.condition_status === 'wash_required' ? 'damaged' : 'sound');
    setReleaseNote('');
  };

  const handleReleaseHold = async () => {
    if (!releasingVisit) return;
    setSaving(true);
    try {
      await api.post(`/depot/visits/${releasingVisit.id}/release-hold`, {
        targetConditionStatus,
        note: releaseNote.trim() || undefined,
      });
      toast.success(`Hold released for ${releasingVisit.container_no}`);
      setReleasingVisit(null);
      setReleaseNote('');
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to release hold');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestApproval = async () => {
    if (!releasingVisit) return;
    setRequestingApproval(true);
    try {
      const approval = await api.post<{ id: string; status: string }>(`/depot/visits/${releasingVisit.id}/request-release-hold-approval`, {
        targetConditionStatus,
        note: releaseNote.trim() || undefined,
      });
      toast.success(`Approval ${approval.id} created`);
      setReleasingVisit(null);
      setReleaseNote('');
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to request hold release approval');
    } finally {
      setRequestingApproval(false);
    }
  };

  return (
    <div className="space-y-5">
      {pageError && <DataErrorBanner error={pageError} entityLabel="Depot Containers" onRetry={refresh} />}

      <div>
        <h2 className="text-lg font-semibold">Container Ledger</h2>
        <p className="text-sm text-muted-foreground">
          Track active container visits, yard status, slot, hold, and last event.
        </p>
      </div>

      <DepotGridPanel title="Active Container Visits" subtitle="One row per visit. Use filters/search later; the default view stays batch-friendly.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Visit / Depot</th>
              <th className={depotThClassName}>Slot</th>
              <th className={depotThClassName}>Status</th>
              <th className={depotThClassName}>Condition / Hold</th>
              <th className={depotThClassName}>Truck / Line</th>
              <th className={depotThClassName}>Last Event</th>
              <th className={depotThClassName}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <DepotEmptyRow colSpan={8}>Loading container visits...</DepotEmptyRow>
            ) : items.length === 0 ? (
              <DepotEmptyRow colSpan={8}>No active container visits yet.</DepotEmptyRow>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.container_no}</td>
                  <td className={depotTdClassName}>
                    <div className="font-medium">{item.visit_ref}</div>
                    <div className="text-xs text-muted-foreground">{item.depot_code ?? '—'}</div>
                  </td>
                  <td className={depotTdClassName}>{item.current_slot_code ?? '—'}</td>
                  <td className={depotTdClassName}>
                    <Badge variant="outline">{item.visit_status}</Badge>
                    <div className="mt-1 text-xs text-muted-foreground">{item.container_size_type ?? '—'} · {item.load_status}</div>
                  </td>
                  <td className={depotTdClassName}>
                    <div>{item.condition_status}</div>
                    <Badge className="mt-1" variant={item.hold_status === 'clear' ? 'secondary' : 'destructive'}>{item.hold_status}</Badge>
                  </td>
                  <td className={depotTdClassName}>
                    <div>{item.truck_plate ?? '—'}</div>
                    <div className="text-xs text-muted-foreground">{item.line_operator ?? '—'}</div>
                  </td>
                  <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.last_event_at)}</td>
                  <td className={depotTdClassName}>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => openHistory(item)}>
                        <Clock3 className="mr-1.5 h-4 w-4" />
                        History
                      </Button>
                      {item.hold_status !== 'clear' && (
                        <Button size="sm" variant="outline" onClick={() => openReleaseHold(item)}>
                          <ShieldAlert className="mr-1.5 h-4 w-4" />
                          Release Hold
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <Dialog open={!!releasingVisit} onOpenChange={(open) => { if (!open) { setReleasingVisit(null); setReleaseNote(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{releasingVisit ? `Release Hold · ${releasingVisit.container_no}` : 'Release Hold'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Select value={targetConditionStatus} onValueChange={(value: 'sound' | 'damaged') => setTargetConditionStatus(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sound">Sound</SelectItem>
                <SelectItem value="damaged">Damaged But Released</SelectItem>
              </SelectContent>
            </Select>
            <Input value={releaseNote} onChange={(event) => setReleaseNote(event.target.value)} placeholder="Repair or wash completion note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReleasingVisit(null); setReleaseNote(''); }}>Cancel</Button>
            <Button variant="outline" onClick={handleRequestApproval} disabled={requestingApproval || saving}>
              {requestingApproval ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Clock3 className="mr-1.5 h-4 w-4" />}
              Request Approval
            </Button>
            <Button onClick={handleReleaseHold} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldAlert className="mr-1.5 h-4 w-4" />}
              Release Hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!historyContainer} onOpenChange={(open) => { if (!open) { setHistoryContainer(null); setHistoryItems([]); setHistoryApprovals([]); } }}>
        <SheetContent className="w-full sm:max-w-[1000px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{historyContainer ? `Container History · ${historyContainer.container_no}` : 'Container History'}</SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            <section className="space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Approvals</div>
              {historyLoading ? (
                <div className="text-sm text-muted-foreground">Loading approval history…</div>
              ) : historyApprovals.length === 0 ? (
                <div className="text-sm text-muted-foreground">No approval records yet.</div>
              ) : (
                <table className={depotTableClassName}>
                  <thead className={depotTableHeadClassName}>
                    <tr>
                      <th className={depotThClassName}>Approval</th>
                      <th className={depotThClassName}>Type</th>
                      <th className={depotThClassName}>Reviewer</th>
                      <th className={depotThClassName}>Status</th>
                      <th className={depotThClassName}>Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {historyApprovals.map((item) => (
                      <tr key={item.id}>
                        <td className={depotTdClassName}>{item.title}</td>
                        <td className={depotTdClassName}>{item.request_type}</td>
                        <td className={depotTdClassName}>{item.reviewer_role ?? '—'}</td>
                        <td className={depotTdClassName}>
                          <Badge variant={item.status === 'approved' ? 'secondary' : item.status === 'pending' ? 'outline' : 'destructive'}>
                            {item.status}
                          </Badge>
                        </td>
                        <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section className="space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Activity</div>
              {historyLoading ? (
                <div className="text-sm text-muted-foreground">Loading activity…</div>
              ) : historyItems.length === 0 ? (
                <div className="text-sm text-muted-foreground">No activity yet.</div>
              ) : (
                <table className={depotTableClassName}>
                  <thead className={depotTableHeadClassName}>
                    <tr>
                      <th className={depotThClassName}>Action</th>
                      <th className={depotThClassName}>Entity</th>
                      <th className={depotThClassName}>Actor</th>
                      <th className={depotThClassName}>Changed Fields</th>
                      <th className={depotThClassName}>Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {historyItems.map((item) => (
                      <tr key={item.id}>
                        <td className={depotTdClassName}>{item.action}</td>
                        <td className={depotTdClassName}>{item.entity_type}</td>
                        <td className={depotTdClassName}>{item.actor_id ?? item.actor_type}</td>
                        <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{item.changes?.fields?.join(', ') ?? '—'}</td>
                        <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
